/* -----------------------------------------------------------------------------------------------------
  - Created: [15-04-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains leave request from employees
  -------------------------------------------------------------------------------------------------------
*/
import React, { useEffect, useState } from 'react';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import LeaveRequestSearch from '../LeaveRequestSearchFilter/LeaveRequestSearch';
import LeaveRequestActionView from '../LeaveRequestActionView/LeaveRequestActionView';
import { get_leaveRequest_with_pagination, update_leaveRequest } from '../../../../config/ManagementApis/LeaveRequestApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import { Icons } from '../../../../utils/GlobalIcons';
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory';
import Loader from '../../../../Components/Loader/Loader';
import { getUserData } from '../../../../utils/localStorage';
import { LeaveInfo } from '../EmployeeAppliedLeaveInfo/LeaveInfo';
import { getLeaveProgressColors } from '../../../../utils/constants';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';

function LeaveRequestsTable() {
    const privileges = useSelector((state) => state.privileges.privileges);
    const UserData = getUserData()
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
    const [refresh, setRefresh] = useState(false);
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });
    const [filterValues, setFilterValues] = useState({
        name: '',
        phone: '',
        leaveType: '',
        start_date: '',
        end_date: '',
        status: '' //-1 commented by sreeraj 9-7-25
    });
    const [remark, setRemark] = useState('');
    const [showLeaveInfo, setShowLeaveInfo] = useState({ isOpen: false, leave: {} });

    const [isLoading, setIsLoading] = useState(false); // Loader state


    useEffect(() => {
        fetchLeaveRequests();
    }, [pageNumber, totalItemPerPage, filterValues, refresh]);

    const fetchLeaveRequests = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        Object.keys(filterValues).forEach(key => {
            formData.append(key, filterValues[key]);
        });

        try {
            setIsLoading(true)
            const response = await get_leaveRequest_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                setLeaveRequests(data.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setLeaveRequests([]);
            }
        } catch (error) {
            setLeaveRequests([]);
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    };

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Leave", 16, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Leave", 16, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Leave", 16, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Leave", 16, PERMISSION_ACTIONS.add),
    };

    const columns = [
        { title: "#", key: "key" },
        { title: "Employee", key: "EmployeeName" },
        { title: "Leave Type", key: "LeaveType" },
        { title: "From", key: "LeaveFrom" },
        { title: "To", key: "LeaveTo" },
        {
            title: "Status",
            key: "Status",
            render: (text, row) => (
                <span style={getLeaveProgressColors(row?.Leavestatus)}>
                    {row?.Status}
                </span>
            ),
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || UserData?.RoleId == 2); //hasActionPermissions

    // FOR HANDLE LEAVE REQUEST APPROVE OR REJECT
    const handleLeaveDecision = async (leaveId, status) => {
        try {
            const response = await update_leaveRequest({ leaveId: leaveId, leaveStatus: status, remark }); // (1-Approve,2 reject)

            if (response?.status === "success") {
                setRefresh(prev => !prev); // Refresh table
                SuccessToast(response?.message);
                closeModal()
            } else {
                ErrorToast(response?.message || 'Something went wrong!');
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        }
    };

    const renderActions = (row) => (
        <div className="action-container">
            <>
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => handleLeaveInfoModal(true, row)} />
                </CustomTooltipWithoutRoundBorder>

                {/* Leavestatus=0 means inprogress */}
                {/* ONLY FOR SUPERVISOR */}
                {UserData?.RoleId === 2 && (
                    row?.Leavestatus === 0 ? (
                        <Icons.Info
                            className="view-action-icon"
                            onClick={() => handleModal(true, row, 'reject_Or_Approve')}
                        />
                    ) : (
                        ""
                    )
                )}
            </>

        </div>
    );

    const rows = () => leaveRequests?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item?.ELD_Id,
        EmployeeName: item?.EmployeeName,
        LeaveType: item?.LeaveType,
        LeaveFrom: item?.LeaveFrom,
        LeaveTo: item?.LeaveTo,
        Status: item?.Status,
        Leavestatus: item?.Leavestatus,
        Leaveremark: item?.Leaveremark || "",
        LeaveReason: item?.leave_reason || "",
        ApprovedBy: item?.ApprovedBy || "",
    }));
    //leaveId
    const handleModal = async (isOpen, leaveData, actionType) => {

        if (actionType !== '') {
            setModalProps({
                isOpen,
                id: leaveData?.id || '',
                formValues: {
                    EmployeeID: leaveData?.id || '',
                    LeaveType: leaveData?.LeaveType || '',
                    LeaveFrom: leaveData?.LeaveFrom || null,
                    LeaveTo: leaveData?.LeaveTo || null,
                    EmployeeName: leaveData?.EmployeeName || '',
                    Status: leaveData?.Status || ''
                }
            });
        }
    };


    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setRemark('')
    };

    const handleRemarkChange = (e) => {
        setRemark(e.target.value);
    };

    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (count) => setTotalItemPerPage(count);
    const handleSearchFilter = (filters) => setFilterValues(filters);

    const handleLeaveInfoModal = (open, leaveInfo) => {
        setShowLeaveInfo({ isOpen: open, leave: leaveInfo });
    };


    return (
        <div className='leave-requests-table-container'>
            <LeaveRequestSearch
                tableTitle="All Leaves"
                searchFilter={handleSearchFilter}
                isAddPermission={userPermissions?.add}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}
            />
            <CustomTable columns={columns} rows={isLoading ? [] : rows()} isLoading={isLoading} />
            <div className='bottom-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}
                />}
            </div>


            {modalProps?.isOpen && <CustomModal
                open={modalProps?.isOpen}
                handleClose={closeModal}
                title="Leave Request Details"
                body={
                    <LeaveRequestActionView
                        remark={remark}
                        onRemarkChange={handleRemarkChange}
                        employeeName={modalProps?.formValues?.EmployeeName}
                        leaveType={modalProps?.formValues?.LeaveType}
                        leaveFrom={modalProps?.formValues?.LeaveFrom}
                        leaveTo={modalProps?.formValues?.LeaveTo}
                        onAccept={() => handleLeaveDecision(modalProps?.id, 1)}
                        onReject={() => handleLeaveDecision(modalProps?.id, 2)}
                    />
                }
                maxWidth="800px"
            />}

            {/*Leave Modal */}
            {showLeaveInfo?.isOpen && <CustomModal
                open={showLeaveInfo?.isOpen}
                handleClose={() => handleLeaveInfoModal(false, {})}
                title={"Leave Details"}
                body={<LeaveInfo leave={showLeaveInfo.leave} />}
                maxWidth="600px"
            />}
        </div>
    );
}

export default LeaveRequestsTable;
